import { MantineProvider } from '@mantine/core';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import type { ComponentPropsWithoutRef } from 'react';
import { useContext, useMemo } from 'react';

import { lightTheme } from '~/config/theme';
import { Loader } from '~/domains/common/components/Loader/Loader';
import { Routes } from '~/domains/common/constants/routes';
import { Breadcrumbs } from '~/domains/design-system/components/Breadcrumbs';
import { useCurrentUserCommissionerChannel } from '~/domains/common/hooks/useCurrentUserCommissionerChannel';
import { UserContext } from '~/components/providers/UserProvider';
import { useCommissionerChannelByHandle } from '~/domains/commissioner/hooks/useCommissionerChannelByHandle';
import { ChannelDetailPage } from '~/domains/channel/pages/ChannelDetailPage';
import { ChannelErrorScreen } from '~/domains/channel/components/ChannelErrorScreen/ChannelErrorScreen';
import { ConnectedChannelHeader } from '~/domains/channel/components/ChannelHeader/ConnectedChannelHeader';
import { ConnectedChannelContests } from '~/domains/channel/components/ChannelContests/ConnectedChannelContests';
import ChannelNavigation from '~/domains/channel/components/ChannelNavigation/ChannelNavigation';
import WelcomeWidget from '~/domains/channel/components/WelcomeWidget/WelcomeWidget';

// needed for og:image
export function getServerSideProps() {
  return { props: {} };
}

function ChannelPage() {
  const { t } = useTranslation('channel');
  const { user } = useContext(UserContext);
  const router = useRouter();
  const { handle, q } = router.query;
  const { data, isLoading } = useCommissionerChannelByHandle({ handle: handle as string });
  const { data: channelData } = useCurrentUserCommissionerChannel();

  const isCurrentUserChannel = channelData?.data?.handle === handle;

  const breadcrumbsLinks = useMemo(
    () =>
      [
        { href: '/', label: t('breadcrumbs.home') },
        ...(q?.length > 0
          ? [{ href: Routes.search(q as string), label: t('breadcrumbs.search') }]
          : []),
        { label: `@${data?.data.handle}` },
      ] satisfies ComponentPropsWithoutRef<typeof Breadcrumbs>['links'],
    [data?.data.handle, q, t]
  );

  if (!handle) {
    router.push(Routes.home());
    return null;
  }

  if (isLoading) {
    return <Loader />;
  }

  if (!data) {
    return <ChannelErrorScreen />;
  }

  return (
    <MantineProvider theme={lightTheme}>
      {!isCurrentUserChannel ? <Breadcrumbs links={breadcrumbsLinks} /> : null}
      <ChannelDetailPage>
        <ChannelDetailPage.Aside>
          <ConnectedChannelHeader
            channel={data?.data}
            isCurrentUserChannel={isCurrentUserChannel}
            isLoggedIn={Boolean(user)}
          />
          {isCurrentUserChannel ? <ChannelDetailPage.CreateContestButton /> : null}
        </ChannelDetailPage.Aside>
        <ChannelDetailPage.Content>
          {isCurrentUserChannel && <WelcomeWidget />}
          <ChannelNavigation
            isCurrentUserChannel={isCurrentUserChannel}
            isSplashAccount={data?.data.isSplashAccount}
          />
          <ConnectedChannelContests
            isCurrentUserChannel={isCurrentUserChannel}
            isSplashAccount={data?.data.isSplashAccount}
            userId={data?.data.userId}
          />
        </ChannelDetailPage.Content>
      </ChannelDetailPage>
    </MantineProvider>
  );
}

export default ChannelPage;

ChannelPage.isPublic = true;
ChannelPage.trackingName = 'Channel Detail Home';
